import type { ReactNode } from 'react';
import {
  createFrontendModule,
  AppRootElementBlueprint,
  createExtension,
  createExtensionInput,
  coreExtensionData,
} from '@backstage/frontend-plugin-api';
import { AppRootWrapperBlueprint, IconBundleBlueprint } from '@backstage/plugin-app-react';
import { SidebarPage } from '@backstage/core-components';
import { useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import AccountTreeIcon from '@material-ui/icons/AccountTree';
import SchoolIcon from '@material-ui/icons/School';
import { TOP_NAV_HEIGHT } from '../components/TopNav';
import { MOBILE_MEDIA_QUERY } from '../components/Root/mobileBreakpoint';
import { BAYER_NAVY, CONTENT_CORNER_RADIUS } from '../theme/bayerTheme';
import { PersonalizationProvider } from '../components/home/personalization';
import { ChatProvider, PopoutProvider, GlobalCollapsibleChat } from '@internal/plugin-mga';
import { OrgProvider } from '@internal/plugin-org-data-react';
import { FeatureFlagsProvider } from '@internal/plugin-feature-flags-common/components';
import { SidebarPreferencesProvider } from '../components/Root/SidebarPreferencesContext';
import { StableVisitListener } from '../components/Root/StableVisitListener';
import { RagModal } from '@internal/plugin-rag-ai';
import { useDataDogRum } from '../components/Root/useDataDogRum';
import { usePostHog } from '../components/Root/usePostHog';
import { useClarity } from '../components/Root/useClarity';
import { useSessionSync } from '../components/Root';
import { useSubmitEmailOnce } from '../hooks/useSubmitEmailOnce';
import { useWhatsNew } from '../hooks/useWhatsNew';
import { WhatsNewDialog } from '../components/whatsnew-dialogue/whatsNewDialogue';
import { TopNav } from '../components/TopNav';
import { CustomOAuthRequestDialog } from '../components/sign-in/CustomOAuthRequestDialog';
import { SignalsDisplay } from '@backstage/plugin-signals';
import { SwaggerUiThemeStyles } from '../components/api-docs/SwaggerUiThemeStyles';
import { EMBED_MODE_CSS, isEmbedMode } from './embedMode';

// ---------------------------------------------------------------------------
// Icon bundle — custom icons used in the sidebar and entity pages.
// ---------------------------------------------------------------------------

const iconBundle = IconBundleBlueprint.make({
  params: {
    icons: {
      accountTree: AccountTreeIcon,
      school: SchoolIcon,
    },
  },
});

const LayoutWrapper = ({ children }: { children: ReactNode }) => {
  // One preferences provider for TopNav + sidebar + pages so user-settings
  // observe subscriptions (and multiget) are not duplicated across trees.
  const content = isEmbedMode() ? (
    <div data-backstage-layout data-embed-mode="true">
      <style>{EMBED_MODE_CSS}</style>
      {children}
    </div>
  ) : (
    <>
      <TopNav />
      <div
        data-backstage-layout
        style={{
          paddingTop: TOP_NAV_HEIGHT,
        }}
      >
          {/*
          Force navy on page-header icons and outlined actions. Theme @global
          rules do not inject here, so use stable MUI selectors scoped to
          [data-backstage-layout] header (excludes the top-nav AppBar).
        */}
          <style>{`
          [data-backstage-layout] header:not([class*="MuiAppBar"]) .MuiSvgIcon-root,
          [data-backstage-layout] header:not([class*="MuiAppBar"]) svg {
            fill: ${BAYER_NAVY};
            color: ${BAYER_NAVY};
          }
          [data-backstage-layout] header:not([class*="MuiAppBar"]) [data-testid="support-button"] {
            color: ${BAYER_NAVY};
          }
          /* Guide / outlined header actions: color="inherit" picks up dark-mode
             body text (white) instead of the gradient header's navy fontColor. */
          [data-backstage-layout] header:not([class*="MuiAppBar"]) .MuiButton-outlined {
            color: ${BAYER_NAVY};
            border-color: ${BAYER_NAVY};
          }
          [data-backstage-layout] header:not([class*="MuiAppBar"]) .MuiButton-outlined:hover {
            border-color: ${BAYER_NAVY};
            background-color: rgba(0, 31, 61, 0.08);
          }
          [data-backstage-layout] .MuiTableContainer-root,
          [data-backstage-layout] .MTableBody-container {
            max-width: 100%;
            overflow-x: auto;
          }
        `}</style>
        {children}
      </div>
    </>
  );

  return (
    <SidebarPreferencesProvider>
      <StableVisitListener />
      {content}
    </SidebarPreferencesProvider>
  );
};

const layoutWrapper = AppRootWrapperBlueprint.make({
  name: 'layout',
  params: {
    component: LayoutWrapper,
  },
});

// ---------------------------------------------------------------------------
// App layout override — gives the content area a rounded top-left corner on
// EVERY route (not just pages built with the core <Page> component).
//
// The default app/layout renders `<SidebarPage>{nav}{content}</SidebarPage>`,
// where `content` is a bare react-router element with no wrapper. We re-render
// the same structure but wrap `content` in a frame that:
//  - sits in normal flow to the RIGHT of the sidebar (SidebarPage adds the
//    left padding), so its top-left is the content-area corner and it tracks
//    the sidebar's pinned/unpinned width automatically;
//  - is the single scroll container below the fixed top nav
//    (height = viewport minus TOP_NAV_HEIGHT; overflow: auto), so its rounded
//    corner is clipped from a fixed frame and stays put on scroll;
//  - shows the navy SidebarPage background through the rounded notch.
// ---------------------------------------------------------------------------

const ContentFrame = ({ children }: { children: ReactNode }) => {
  const theme = useTheme();
  const topOffset = isEmbedMode() ? 0 : TOP_NAV_HEIGHT;
  const isMobile = useMediaQuery(MOBILE_MEDIA_QUERY, { noSsr: true });
  const bottomOffset = isEmbedMode() || !isMobile ? 0 : TOP_NAV_HEIGHT;
  return (
    <div
      style={{
        height: `calc(100vh - ${topOffset + bottomOffset}px)`,
        overflowY: 'auto',
        overflowX: 'hidden',
        minWidth: 0,
        maxWidth: '100%',
        // Follow the active theme so the pane is white in light mode and
        // dark in dark mode (was previously hardcoded white).
        backgroundColor: theme.palette.background.default,
        borderTopLeftRadius: isEmbedMode() ? 0 : CONTENT_CORNER_RADIUS,
      }}
    >
      {children}
    </div>
  );
};

const appLayout = createExtension({
  // Must stay 'layout' so this overrides @backstage/plugin-app's built-in
  // app/layout extension; the framework routes nav/content inputs to that id.
  name: 'layout',
  attachTo: { id: 'app/root', input: 'children' },
  inputs: {
    nav: createExtensionInput([coreExtensionData.reactElement], {
      singleton: true,
    }),
    content: createExtensionInput([coreExtensionData.reactElement], {
      singleton: true,
    }),
  },
  output: [coreExtensionData.reactElement],
  factory: ({ inputs }) => [
    coreExtensionData.reactElement(
      <SidebarPage>
        {inputs.nav.get(coreExtensionData.reactElement)}
        <ContentFrame>
          {inputs.content.get(coreExtensionData.reactElement)}
        </ContentFrame>
      </SidebarPage>,
    ),
  ],
});

// ---------------------------------------------------------------------------
// App root wrappers — each wraps the entire app; order in the React tree is
// not guaranteed, so each must be fully independent of the others.
// ---------------------------------------------------------------------------

const personalizationWrapper = AppRootWrapperBlueprint.make({
  name: 'personalization',
  params: {
    component: ({ children }) => (
      <PersonalizationProvider>{children}</PersonalizationProvider>
    ),
  },
});

const orgWrapper = AppRootWrapperBlueprint.make({
  name: 'org',
  params: {
    component: ({ children }) => (
      <OrgProvider>{children}</OrgProvider>
    ),
  },
});

const featureFlagsWrapper = AppRootWrapperBlueprint.make({
  name: 'feature-flags',
  params: {
    component: ({ children }) => (
      <FeatureFlagsProvider>{children}</FeatureFlagsProvider>
    ),
  },
});

const mgaChatWrapper = AppRootWrapperBlueprint.make({
  name: 'mga-chat',
  params: {
    component: ({ children }) => (
      <ChatProvider>
        <PopoutProvider>{children}</PopoutProvider>
      </ChatProvider>
    ),
  },
});

// ---------------------------------------------------------------------------
// App root elements — rendered adjacent to the app layout (inside AppRouter).
// ---------------------------------------------------------------------------

// RagModal and GlobalCollapsibleChat share a single ChatProvider + PopoutProvider
// instance here. AppRootWrapperBlueprint only wraps page-route content, so these
// elements must carry their own shared provider rather than relying on mgaChatWrapper.
const mgaRootElement = AppRootElementBlueprint.make({
  name: 'mga-root',
  params: {
    element: (
      <ChatProvider>
        <PopoutProvider>
          <RagModal />
          <GlobalCollapsibleChat />
        </PopoutProvider>
      </ChatProvider>
    ),
  },
});

const signalsDisplayElement = AppRootElementBlueprint.make({
  name: 'signals-display',
  params: {
    element: <SignalsDisplay />,
  },
});

/** Replaces stock dialog: close (x) instead of "Reject All". */
const oauthRequestDialogElement = AppRootElementBlueprint.makeWithOverrides({
  name: 'oauth-request-dialog',
  factory(originalFactory) {
    return originalFactory({
      element: <CustomOAuthRequestDialog />,
    });
  },
});

const DataDogRumComponent = () => {
  useDataDogRum();
  return null;
};
const dataDogRumElement = AppRootElementBlueprint.make({
  name: 'datadog-rum',
  params: { element: <DataDogRumComponent /> },
});

const PostHogInitComponent = () => {
  usePostHog();
  return null;
};
const postHogInitElement = AppRootElementBlueprint.make({
  name: 'posthog-init',
  params: { element: <PostHogInitComponent /> },
});

const ClarityInitComponent = () => {
  useClarity();
  return null;
};
const clarityInitElement = AppRootElementBlueprint.make({
  name: 'clarity-init',
  params: { element: <ClarityInitComponent /> },
});

const SessionSyncComponent = () => {
  useSessionSync();
  return null;
};
const sessionSyncElement = AppRootElementBlueprint.make({
  name: 'session-sync',
  params: { element: <SessionSyncComponent /> },
});

const EmailSyncComponent = () => {
  useSubmitEmailOnce();
  return null;
};
const emailSyncElement = AppRootElementBlueprint.make({
  name: 'email-sync',
  params: { element: <EmailSyncComponent /> },
});

const WhatsNewControllerComponent = () => {
  const { open, close, latestRelease, loading, error, hasNewVersion, refresh, majorGroups, autoPopupDisabled, setAutoPopupPreference } =
    useWhatsNew();
  return (
    <WhatsNewDialog
      open={open}
      latestRelease={latestRelease}
      loading={loading}
      error={error}
      hasNewVersion={hasNewVersion}
      onClose={close}
      onRefresh={refresh}
      majorGroups={majorGroups}
      autoPopupDisabled={autoPopupDisabled}
      onAutoPopupChange={setAutoPopupPreference}
    />
  );
};
const whatsNewControllerElement = AppRootElementBlueprint.make({
  name: 'whats-new-controller',
  params: { element: <WhatsNewControllerComponent /> },
});

const swaggerUiThemeStylesElement = AppRootElementBlueprint.make({
  name: 'swagger-ui-theme-styles',
  params: { element: <SwaggerUiThemeStyles /> },
});

// ---------------------------------------------------------------------------
// App root module
// ---------------------------------------------------------------------------

export const appRootModule = createFrontendModule({
  pluginId: 'app',
  extensions: [
    iconBundle,
    layoutWrapper,
    appLayout,
    personalizationWrapper,
    orgWrapper,
    featureFlagsWrapper,
    mgaChatWrapper,
    mgaRootElement,
    signalsDisplayElement,
    oauthRequestDialogElement,
    dataDogRumElement,
    postHogInitElement,
    clarityInitElement,
    sessionSyncElement,
    emailSyncElement,
    whatsNewControllerElement,
    swaggerUiThemeStylesElement,
  ],
});
